import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import SearchHero from "../components/home/SearchHero";
import CategoryStrip from "../components/home/CategoryStrip";
import ProductRail from "../components/home/ProductRail";
import FeaturedCategories from "../components/home/FeaturedCategories";
import TrustBar from "../components/home/TrustBar";
import ModelSelectorModal from "../components/ModelSelectorModal";
import { trackEvent } from "../utils/analytics";
import { useVehicle } from "../context/VehicleContext";
import { categories, trendingParts } from "../data/homeData";
import { fitmentData } from "../data/fitmentData";

/* ── Static styles moved outside component so they're never recreated ── */
const staticStyles = {
  page: {
    background: "#F6F7FB",
    minHeight: "100vh",
    fontFamily: "Inter, sans-serif"
  },
  vehicleBar: {
    position: "absolute",
    top: 82,
    right: 28,
    zIndex: 25,
  },
  vehicleButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
    minWidth: 270,
    padding: "11px 14px",
    borderRadius: 18,
    border: "1px solid rgba(0,0,0,0.06)",
    background: "rgba(255,255,255,0.88)",
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
    cursor: "pointer",
    boxShadow: "0 10px 28px rgba(0,0,0,0.08)",
    textAlign: "left",
    transition: "all .18s ease",
  },
  vehicleTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "#111827",
    lineHeight: "15px",
  },
  vehicleSub: {
    fontSize: 10.5,
    color: "#9CA3AF",
    marginTop: 2,
  },
  vehicleArrow: {
    fontSize: 14,
    color: "#9CA3AF",
    opacity: 0.75,
  },
  vehicleClear: {
    width: 24,
    height: 24,
    borderRadius: 999,
    background: "rgba(0,0,0,0.06)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 700,
    color: "#6B7280",
    transition: "all .18s ease",
    cursor: "pointer",
  },
  contactGlassCard: {
    marginTop: 34,
    width: 300,
    height: 160,
    borderRadius: 34,
    background: "linear-gradient(145deg, rgba(255,255,255,0.96), rgba(245,245,247,0.92))",
    border: "1px solid rgba(255,255,255,0.95)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    padding: "24px 26px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    transition: "all .25s ease",
  },
  contactGlassTop: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "1.4px",
    color: "#A1A1AA",
  },
  contactGlassTitle: {
    fontSize: 34,
    fontWeight: 700,
    letterSpacing: "-1.8px",
    color: "#111111",
    lineHeight: 1,
    marginTop: 10,
    marginBottom: 10,
  },
  contactGlassSub: {
    fontSize: 14,
    lineHeight: "23px",
    color: "#6E6E73",
    fontWeight: 500,
    letterSpacing: "-0.2px",
  },
  contactGlassArrowWrap: {
    display: "flex",
    justifyContent: "flex-end",
  },
  contactGlassArrow: {
    width: 46,
    height: 46,
    borderRadius: 999,
    background: "#111111",
    color: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    fontWeight: 600,
  },
  footer: {
    textAlign: "center",
    padding: "50px 20px 40px",
    color: "#8E8E93",
    fontSize: "13px"
  },
  footerDivider: {
    width: "90%",
    height: "1px",
    background: "#E5E5EA",
    margin: "0 auto 20px"
  },
  footerLinks: {
    marginTop: "6px",
    fontSize: "13px"
  },
  footerLink: {
    margin: "0 6px",
    color: "#8E8E93",
    textDecoration: "none",
    cursor: "pointer"
  },
  container: {
    maxWidth: "1300px",
    margin: "0 auto",
    padding: "20px"
  }
};



const makes = Object.keys(fitmentData);
const models = Object.fromEntries(
  Object.entries(fitmentData).map(([make, yearMap]) => [
    make,
    [...new Set(Object.values(yearMap).flat())]
  ])
);
const years = [...new Set(
  Object.values(fitmentData).flatMap(yearMap => Object.keys(yearMap).map(Number))
)].sort((a, b) => b - a);

export default function HomeScreen() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [listings, setListings] = useState([]);
  const [contactHovered, setContactHovered] = useState(false);
  const [modelSelectorOpen, setModelSelectorOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMake, setSelectedMake] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedSystem, setSelectedSystem] = useState(null);
  const [selectedSubSystem, setSelectedSubSystem] = useState(null);

  const { selectedVehicle, vehicleLoaded, setSelectedVehicle } = useVehicle();

  useEffect(() => {
    trackEvent("site_visit");
  }, []);

  useEffect(() => {
    if (!vehicleLoaded) return;

    let url = "https://eleven11-i6v4.onrender.com/api/listings";

    if (selectedVehicle) {
      const params = new URLSearchParams({
        year: selectedVehicle.year,
        manufacturer: selectedVehicle.manufacturer,
        model: selectedVehicle.model,
      });
      url += `?${params.toString()}`;
    }

    fetch(url)
      .then(res => res.json())
      .then(data => {
        const shuffled = [...data].sort(() => Math.random() - 0.5);
        setListings(shuffled);
      })
      .catch(err => console.error(err));

  }, [selectedVehicle, vehicleLoaded]);

  const usedOEMTop4 = useMemo(() =>
    listings.filter(l => l.condition === "used").slice(0, 4),
    [listings]
  );

  const newOEMTop4 = useMemo(() =>
    listings.filter(l => l.condition === "new").slice(0, 4),
    [listings]
  );

  const sprays = useMemo(() =>
    listings.filter(l => l.category === "Lubricants & Sprays").slice(0, 4),
    [listings]
  );

  const handleSearch = useCallback((customQuery) => {
    const finalQuery = (customQuery || searchQuery).trim();
    if (!finalQuery) return;

    let url = `/search?q=${encodeURIComponent(finalQuery)}`;

    if (selectedVehicle) {
      const params = new URLSearchParams({
        manufacturer: selectedVehicle.manufacturer,
        model: selectedVehicle.model,
        year: selectedVehicle.year,
      });
      url += `&${params.toString()}`;
    }

    navigate(url);
  }, [searchQuery, selectedVehicle, navigate]);

  const handleClearVehicle = useCallback((e) => {
    e.stopPropagation();
    setSelectedVehicle(null);
    localStorage.removeItem("selectedVehicle");
  }, [setSelectedVehicle]);

  const contactCardStyle = useMemo(() => ({
    ...staticStyles.contactGlassCard,
    transform: contactHovered ? "translateY(-8px) scale(1.025)" : "translateY(0px) scale(1)",
    boxShadow: contactHovered
      ? `0 1px 1px rgba(255,255,255,0.9) inset, 0 24px 48px rgba(0,0,0,0.08), 0 60px 120px rgba(0,0,0,0.08), 0 120px 180px rgba(0,0,0,0.05)`
      : `0 1px 1px rgba(255,255,255,0.9) inset, 0 12px 24px rgba(0,0,0,0.04), 0 30px 60px rgba(0,0,0,0.06), 0 60px 120px rgba(0,0,0,0.04)`,
    cursor: "pointer",
  }), [contactHovered]);

  return (
    <div style={staticStyles.page}>

      <SearchHero
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onSearch={handleSearch}
      />

      <div style={staticStyles.vehicleBar}>
        <button
          style={staticStyles.vehicleButton}
          onClick={() => setModelSelectorOpen(true)}
        >
          <div>
            <div style={staticStyles.vehicleTitle}>
              {selectedVehicle
                ? `${selectedVehicle.year} ${selectedVehicle.manufacturer} ${selectedVehicle.model}`
                : "Select Year / Make / Model"}
            </div>
            <div style={staticStyles.vehicleSub}>
              {selectedVehicle ? "Vehicle Selected" : "Find Parts That Fit Your Ski"}
            </div>
          </div>

          {selectedVehicle ? (
            <div onClick={handleClearVehicle} style={staticStyles.vehicleClear}>×</div>
          ) : (
            <div style={staticStyles.vehicleArrow}>→</div>
          )}
        </button>
      </div>

      <div style={staticStyles.container}>
        <ProductRail title="USED OEM PARTS" items={usedOEMTop4} />
      </div>

      <div style={staticStyles.container}>
        <FeaturedCategories />
      </div>

      <div style={staticStyles.container}>
        <CategoryStrip items={categories} />
        <ProductRail title="Lubricants & Sprays" items={sprays} />
        <ProductRail title="BRAND NEW OEM PARTS" items={newOEMTop4} />
      </div>

      <TrustBar />

      {/* PREMIUM BRAND SECTION */}
      <div style={{ width: "100%", background: "transparent", borderTop: "1px solid rgba(0,0,0,0.04)", marginTop: 70 }}>
        <div style={{ maxWidth: 1400, margin: "0 auto", padding: "84px 60px 70px" }}>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 80, flexWrap: "wrap" }}>

            {/* LEFT */}
            <div style={{ maxWidth: 700 }}>
              <div style={{ fontSize: 64, fontWeight: 700, letterSpacing: "-3px", color: "#0A0A0A", lineHeight: 0.94, marginBottom: 26 }}>
                ELEVEN
              </div>
              <div style={{ fontSize: 30, lineHeight: "42px", letterSpacing: "-1px", color: "#6E6E73", fontWeight: 400, maxWidth: 520 }}>
                OEM marine distribution, reimagined.
              </div>

              <div style={{ display: "flex", gap: 18, marginTop: 46, flexWrap: "wrap" }}>
                {["YAMAHA", "SEA-DOO", "KAWASAKI", "JETPILOT", "NGK", "AMSOIL"].map((brand) => (
                  <div key={brand} style={{
                    minWidth: 120, height: 52, padding: "0 22px", borderRadius: 999,
                    background: "rgba(255,255,255,0.42)", backdropFilter: "blur(24px)",
                    WebkitBackdropFilter: "blur(24px)", border: "1px solid rgba(255,255,255,0.55)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "#111111", fontSize: 12, fontWeight: 700, letterSpacing: "1.8px",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), 0 6px 20px rgba(0,0,0,0.04)",
                    transform: "translateZ(0)", transition: "all .25s ease", cursor: "pointer",
                  }}>
                    {brand}
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 18, paddingTop: 4 }}>
              <a style={{ fontSize: 12, fontWeight: 700, letterSpacing: "1.8px", color: "#111111", textTransform: "uppercase", textDecoration: "none" }}>
                elevensalesinfo@gmail.com
              </a>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "1.8px", color: "#111111", textTransform: "uppercase" }}>
                (305) 783-6077
              </div>

              <div
                onMouseEnter={() => setContactHovered(true)}
                onMouseLeave={() => setContactHovered(false)}
                onClick={() => navigate("/contact")}
                style={contactCardStyle}
              >
                <div style={staticStyles.contactGlassTop}>GET IN TOUCH</div>
                <div>
                  <div style={staticStyles.contactGlassTitle}>Contact Us</div>
                  <div style={staticStyles.contactGlassSub}>
                    Speak with our parts, products, logistics, or dealer support team.
                  </div>
                </div>
                <div style={staticStyles.contactGlassArrowWrap}>
                  <div style={staticStyles.contactGlassArrow}>→</div>
                </div>
              </div>
            </div>

          </div>

          {/* BOTTOM */}
          <div style={{ marginTop: 70, paddingTop: 22, borderTop: "1px solid rgba(0,0,0,0.05)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 18 }}>
            <div style={{ fontSize: 12, color: "#A1A1AA", fontWeight: 500, letterSpacing: "-0.1px" }}>
              © 2026 ELEVEN Industries
            </div>
            <div style={{ display: "flex", gap: 24 }}>
            {[
  { label: "Privacy", path: "/privacy" },
  { label: "Terms", path: "/terms" },
  { label: "Wholesale", path: "/contact" },
  { label: "Contact Us", path: "/contact" },
  { label: "ELEVEN Dealer Program", path: "/contact" },
].map((item) => (
  <div key={item.label} onClick={() => navigate(item.path)} style={{ fontSize: 12, color: "#A1A1AA", fontWeight: 500, cursor: "pointer", letterSpacing: "-0.1px" }}>
    {item.label}
  </div>

              ))}
            </div>
          </div>

        </div>
      </div>

  

      <ModelSelectorModal
        diagramMode={false}
        modelSelectorOpen={modelSelectorOpen}
        setModelSelectorOpen={setModelSelectorOpen}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        selectedMake={selectedMake}
        setSelectedMake={setSelectedMake}
        selectedModel={selectedModel}
        setSelectedModel={setSelectedModel}
        selectedSystem={selectedSystem}
        setSelectedSystem={setSelectedSystem}
        selectedSubSystem={selectedSubSystem}
        setSelectedSubSystem={setSelectedSubSystem}
        makes={makes}
        models={models}
        years={years}
      />

    </div>
  );
}